import React from 'react';
import {
	type AutoListCommonExtendProps,
	AutoListField,
} from '@netx/core-components/AutoListField';
import {
	useAutoList,
	type UseAutoListParams,
} from '@netx/core-next/hooks/autoList/useAutoList';
import {splitEventHandler} from '@netx/core-next/utils/fn';
import {vocabularyValueLookup} from '../../api';

const DEFAULT_DATA: any[] = [];

export interface AttributeVocabularyLookupProps<
	Multiple extends boolean = true,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean = false,
> extends AutoListCommonExtendProps<
		string,
		Multiple,
		DisableClearable,
		FreeSolo
	> {
	vocabularyId: number;
	filterResponseOptions?: UseAutoListParams<
		typeof vocabularyValueLookup
	>['filterOptions'];
}

export const AttributeVocabularyLookupPrimitive = <
	Multiple extends boolean = true,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean = false,
>({
	vocabularyId,
	filterResponseOptions,
	onClose,
	onOpen,
	onInputChange,
	...props
}: AttributeVocabularyLookupProps<Multiple, DisableClearable, FreeSolo>) => {
	const {loadingOptions, query, queryStr, setQueryStr, setIsOpen} = useAutoList(
		{
			params: {id: vocabularyId},
			queryFn: vocabularyValueLookup,
			filterOptions: filterResponseOptions,
		},
	);

	return (
		<AutoListField<string, Multiple, DisableClearable, FreeSolo>
			loadingOptions={loadingOptions}
			{...props}
			inputValue={queryStr}
			options={query.data ?? DEFAULT_DATA}
			onClose={splitEventHandler(onClose, () => setIsOpen(false))}
			onOpen={splitEventHandler(onOpen, () => setIsOpen(true))}
			onInputChange={splitEventHandler(onInputChange, (event, value, reason) =>
				setQueryStr(value),
			)}
		/>
	);
};

export const AttributeVocabularyLookup = React.memo(
	AttributeVocabularyLookupPrimitive,
) as <
	Multiple extends boolean = true,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean = false,
>(
	props: AttributeVocabularyLookupProps<Multiple, DisableClearable, FreeSolo>,
) => JSX.Element;

export default AttributeVocabularyLookup;
